/**
 * 射影変換(ホモグラフィ)の計算。
 *
 * 設計書 8.4 のとおり、動画を 4 頂点クアッドへ載せるには射影変換が要る。
 * Canvas 2D のアフィン変換では 4 点を自由に指定できないため、3x3 の
 * ホモグラフィ行列を解いて WebGL のシェーダへ渡す([ADR-0007](../../../docs/adr/0007-projection-rendering-webgl.md))。
 *
 * 行列の並びは**行優先**の 9 要素 `[a, b, c, d, e, f, g, h, i]`:
 *
 * ```
 *   | a b c |     x' = (a x + b y + c) / (g x + h y + i)
 *   | d e f |     y' = (d x + e y + f) / (g x + h y + i)
 *   | g h i |
 * ```
 *
 * **WebGL の `uniformMatrix3fv` は列優先を期待する**ため、渡すときは
 * `toColumnMajor` で並べ替える —— ここを間違えると転置された変換になり、
 * 「なぜか斜めにずれる」という追いにくい不具合になる。
 */
/** ピボットがこれ未満なら特異とみなす。 */
const SINGULAR_EPSILON = 1e-12;
/** 4 点が退化していて変換を解けない。 */
export class DegenerateQuadError extends Error {
    constructor() {
        super('4 頂点が退化しています(3 点以上が同一直線上にある可能性があります)');
        this.name = 'DegenerateQuadError';
    }
}
/**
 * 連立一次方程式 `A x = b` をガウスの消去法で解く。
 *
 * **部分ピボット選択を行う。** 小さなピボットで割ると誤差が急激に膨らむため、
 * 各列で絶対値が最大の行を選ぶ。
 */
function solve(a, b) {
    const n = b.length;
    // 拡大係数行列を作る(引数を破壊しない)。
    const m = a.map((row, i) => [...row, b[i] ?? 0]);
    for (let col = 0; col < n; col += 1) {
        let pivot = col;
        for (let row = col + 1; row < n; row += 1) {
            if (Math.abs(m[row]?.[col] ?? 0) > Math.abs(m[pivot]?.[col] ?? 0))
                pivot = row;
        }
        if (Math.abs(m[pivot]?.[col] ?? 0) < SINGULAR_EPSILON)
            throw new DegenerateQuadError();
        if (pivot !== col) {
            const tmp = m[col];
            const other = m[pivot];
            if (tmp === undefined || other === undefined)
                throw new DegenerateQuadError();
            m[col] = other;
            m[pivot] = tmp;
        }
        const pivotRow = m[col];
        if (pivotRow === undefined)
            throw new DegenerateQuadError();
        const p = pivotRow[col] ?? 0;
        for (let row = col + 1; row < n; row += 1) {
            const target = m[row];
            if (target === undefined)
                continue;
            const factor = (target[col] ?? 0) / p;
            if (factor === 0)
                continue;
            for (let c = col; c <= n; c += 1) {
                target[c] = (target[c] ?? 0) - factor * (pivotRow[c] ?? 0);
            }
        }
    }
    // 後退代入。
    const x = new Array(n).fill(0);
    for (let row = n - 1; row >= 0; row -= 1) {
        const current = m[row];
        if (current === undefined)
            throw new DegenerateQuadError();
        let sum = current[n] ?? 0;
        for (let c = row + 1; c < n; c += 1)
            sum -= (current[c] ?? 0) * (x[c] ?? 0);
        x[row] = sum / (current[row] ?? 1);
    }
    return x;
}
/**
 * 4 点対応からホモグラフィを求める。
 *
 * `src` / `dst` は同じ順序で対応させる(左上 → 右上 → 右下 → 左下)。
 * `i = 1` に固定し、8 未知数の連立方程式を解く。各対応点が 2 本の式を与える:
 *
 * ```
 *   a x + b y + c - u g x - u h y = u
 *   d x + e y + f - v g x - v h y = v
 * ```
 *
 * @throws {DegenerateQuadError} 4 点が退化している場合
 */
export function computeHomography(src, dst) {
    if (src.length !== 4 || dst.length !== 4) {
        throw new RangeError('src / dst はそれぞれ 4 点必要です');
    }
    const a = [];
    const b = [];
    for (let i = 0; i < 4; i += 1) {
        const [x, y] = src[i] ?? [0, 0];
        const [u, v] = dst[i] ?? [0, 0];
        a.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
        b.push(u);
        a.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
        b.push(v);
    }
    const [p, q, r, s, t, u, v, w] = solve(a, b);
    return [p ?? 0, q ?? 0, r ?? 0, s ?? 0, t ?? 0, u ?? 0, v ?? 0, w ?? 0, 1];
}
/**
 * 矩形 `[0,0]-[width,height]` を 4 点クアッドへ写す変換。
 *
 * **盤面の座標を表示座標に載せる向き**(#147 で写し元を動画の矩形から
 * 盤面の矩形に変えた —— [ADR-0028](../../../docs/adr/0028-video-fit-and-panel-aspect.md))。
 * シェーダでは**逆向き**(表示 → 盤面)が必要なので `invert` を通す。
 */
export function rectToQuad(width, height, quad) {
    return computeHomography([
        [0, 0],
        [width, 0],
        [width, height],
        [0, height],
    ], quad);
}
/**
 * 3x3 の逆行列。
 *
 * @throws {DegenerateQuadError} 行列式が 0 に近い場合
 */
export function invert(m) {
    const [a, b, c, d, e, f, g, h, i] = m;
    const cofactor00 = e * i - f * h;
    const cofactor01 = f * g - d * i;
    const cofactor02 = d * h - e * g;
    const det = a * cofactor00 + b * cofactor01 + c * cofactor02;
    if (Math.abs(det) < SINGULAR_EPSILON)
        throw new DegenerateQuadError();
    // 余因子行列の転置 / det。
    return [
        cofactor00 / det,
        (c * h - b * i) / det,
        (b * f - c * e) / det,
        cofactor01 / det,
        (a * i - c * g) / det,
        (c * d - a * f) / det,
        cofactor02 / det,
        (b * g - a * h) / det,
        (a * e - b * d) / det,
    ];
}
/** 点に変換を適用する(同次座標で割る)。 */
export function apply(m, point) {
    const [x, y] = point;
    const w = m[6] * x + m[7] * y + m[8];
    // w が 0 に近いのは点が無限遠に写る場合。呼び出し側で描画対象から外れる。
    if (Math.abs(w) < SINGULAR_EPSILON)
        return [Number.NaN, Number.NaN];
    return [(m[0] * x + m[1] * y + m[2]) / w, (m[3] * x + m[4] * y + m[5]) / w];
}
/**
 * 列優先に並べ替える。
 *
 * **`gl.uniformMatrix3fv` は列優先を期待する。** 行優先のまま渡すと
 * 転置された変換になり、見た目は「それらしく歪む」ので気付きにくい。
 */
export function toColumnMajor(m) {
    return new Float32Array([m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]]);
}
//# sourceMappingURL=homography.js.map